import { IEventLoggingComponentProps, withEventLogging } from '@AppModels/Logging';
import { IUser } from '@ViewModels';
import { StyleDeclarationValue, css } from 'aphrodite';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { IModalContext, ModalChildComponentContextKey } from '../../../../models';
import { IAddEmployeeResult } from '../../../../models/AdminModels';
import {
	ErrorMessagesViewModelKey,
	IErrorMessageComponentProps,
	UserSessionViewModelKey,
} from '../../../../models/AppState';
import { Checkbox } from '../../../../web/components/Checkbox';
import { asModalComponent } from '../../../../web/components/Modal';
import { TextInput } from '../../../../web/components/TextInput';
import { baseStyleSheet } from '../../../../web/styles/styles';
import { styleSheet } from './styles';

interface IProps extends IEventLoggingComponentProps, IModalContext<IAddEmployeeResult>, IErrorMessageComponentProps {
	className?: string;
	styles?: StyleDeclarationValue[];
}

interface IState {
	email?: string;
	firstName?: string;
	lastName?: string;
	sendWelcomeEmail?: boolean;
}

class _AddEmployee extends React.Component<IProps, IState> {
	constructor(props: IProps) {
		super(props);
		this.state = {
			sendWelcomeEmail: false,
		};
	}

	public render() {
		const { className, styles } = this.props;
		const { sendWelcomeEmail } = this.state;
		return (
			<div className={`${css(styleSheet.container, ...(styles || []))} add-employee ${className || ''}`}>
				<div className={css(styleSheet.header)}>Add Employee</div>
				<div className={css(styleSheet.inputs)}>
					{this.renderInputField('firstName', 'First Name')}
					{this.renderInputField('lastName', 'Last Name')}
					{this.renderInputField('email', 'Email')}
				</div>
				<div className={css(styleSheet.footer)}>
					<button className={css(baseStyleSheet.ctaButton)} onClick={this.onAddButtonClicked}>
						<span>Add</span>
					</button>
					<Checkbox
						id='add-employee-send-welcome-checkbox'
						onChange={this.onSendEmailChanged}
						checked={!!sendWelcomeEmail}
					>
						<span>Send welcome email</span>
					</Checkbox>
				</div>
			</div>
		);
	}

	private renderInputField(key: keyof IState, placeholder?: string) {
		return (
			<TextInput
				className={css(styleSheet.input)}
				inputId={`add-employee-${key}`}
				onChange={this.onInputFieldChanged(key)}
				placeholder={placeholder}
				type='text'
				value={`${this.state[key] || ''}`}
			/>
		);
	}

	private onSendEmailChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
		this.setState({
			sendWelcomeEmail: !!e.target.checked,
		});
	};

	private onInputFieldChanged = (key: keyof IState) => (e: React.ChangeEvent<HTMLInputElement>) => {
		this.setState({
			[key]: e.target.value || '',
		});
	};

	private onAddButtonClicked = () => {
		const { errorMessages, parentModal } = this.props;
		const { email, firstName, lastName, sendWelcomeEmail } = this.state;

		let errorMessage: string = null;
		const trimmedEmail = (email || '').trim();
		const trimmedFirstName = (firstName || '').trim();
		const trimmedLastName = (lastName || '').trim();

		if (!trimmedFirstName) {
			errorMessage = 'Please provide a valid first name';
		}
		if (!errorMessage && !trimmedLastName) {
			errorMessage = 'Please provide a valid last name';
		}
		if (!errorMessage && !trimmedEmail) {
			errorMessage = 'Please provide a valid email';
		}

		if (errorMessage) {
			errorMessages.push({
				messages: [errorMessage],
			});
			return;
		}

		if (parentModal) {
			const user: IUser = {
				firstName: trimmedFirstName,
				lastName: trimmedLastName,
				primaryEmail: {
					value: trimmedEmail,
				},
			};
			parentModal.onRequestClose({ sendWelcomeEmail, user }, false);
		}
	};
}

const AddEmployeeAsObserver = observer(_AddEmployee);
const AddEmployeeWithContext = inject(
	UserSessionViewModelKey,
	ErrorMessagesViewModelKey,
	ModalChildComponentContextKey
)(AddEmployeeAsObserver);
const AddEmployee = withEventLogging(AddEmployeeWithContext, 'AddEmployee');

export const AddEmployeeModal = asModalComponent(AddEmployee, {
	className: css(styleSheet.modal),
	shouldCloseOnOverlayClick: false,
	useDefaultHeader: true,
});
